import React, { useState } from "react";
import {
  DemoDataTable,
  DemoResponsiveGrid,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  type DemoPillVariant,
} from "@csk/demo-ui";
import {
  BOUNDARY_LABELS,
  FLEET_SYSTEMS,
  INITIAL_BOUNDARIES,
  boundaryAt,
  boundaryShortLabel,
  summarizeFleetSecurity,
  toggleBoundaryCell,
  type BoundaryType,
} from "./fleet-security-panel/fleetSecurityModel.js";

const BOUNDARY_VARIANT: Record<BoundaryType, DemoPillVariant> = {
  gateway: "success",
  quarantine: "warning",
  trust_boundary: "seam",
  open: "danger",
};

const FleetSecurityPanel = (): React.JSX.Element => {
  const [boundaries, setBoundaries] = useState(INITIAL_BOUNDARIES);
  const metrics = summarizeFleetSecurity(FLEET_SYSTEMS, boundaries);
  const toggleBoundary = (i: number, j: number): void => {
    setBoundaries((previous) => toggleBoundaryCell(previous, i, j));
  };

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard title="Fleet Security Composition (§17)">
        <DemoWorkbenchCopy>
          Click boundary cells to cycle: Gateway → Quarantine → Trust Wall → OPEN
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={150} gap={8}>
        {FLEET_SYSTEMS.map((sys) => (
          <DemoWorkbenchCard
            key={sys.name}
            compact
            title={sys.name}
            description={`R${sys.regime} · κ=${sys.kappa}`}
            right={
              <DemoWorkbenchPill variant={sys.certified ? "success" : "danger"}>
                {sys.certified ? "certified" : "uncertified"}
              </DemoWorkbenchPill>
            }
            style={{
              background: sys.certified
                ? "rgba(20,83,45,0.1)"
                : "rgba(127,29,29,0.1)",
              borderColor: sys.certified
                ? "rgba(34,197,94,0.3)"
                : "rgba(239,68,68,0.3)",
            }}
          >
            <span className="sr-only">Fleet system certification status.</span>
          </DemoWorkbenchCard>
        ))}
      </DemoResponsiveGrid>

      <DemoWorkbenchCard compact>
        <DemoWorkbenchCopy tone="muted" className="mb-2">
          Boundary Graph (click to change)
        </DemoWorkbenchCopy>
        <DemoDataTable
          compact
          tone="dark"
          minWidth={520}
          ariaLabel="Fleet boundary graph"
          rows={FLEET_SYSTEMS}
          getRowKey={(system) => system.name}
          columns={[
            {
              id: "system",
              header: "",
              cell: (system) => (
                <span className="font-mono text-gray-400">
                  {system.name.slice(0, 8)}
                </span>
              ),
            },
            ...FLEET_SYSTEMS.map((target, targetIndex) => ({
              id: target.name,
              header: target.name.slice(0, 8),
              align: "center" as const,
              cell: (
                _source: (typeof FLEET_SYSTEMS)[number],
                sourceIndex: number,
              ): React.ReactNode => {
                const upper = boundaryAt(boundaries, sourceIndex, targetIndex);
                const lower = boundaryAt(boundaries, targetIndex, sourceIndex);
                if (sourceIndex === targetIndex) {
                  return <span className="text-gray-600">—</span>;
                }
                if (sourceIndex < targetIndex) {
                  return (
                    <DemoWorkbenchPill
                      variant={BOUNDARY_VARIANT[upper]}
                      ariaPressed={false}
                      onClick={() => {
                        toggleBoundary(sourceIndex, targetIndex);
                      }}
                    >
                      {boundaryShortLabel(upper)}
                    </DemoWorkbenchPill>
                  );
                }
                return (
                  <DemoWorkbenchPill
                    variant={BOUNDARY_VARIANT[lower]}
                    className="opacity-50"
                  >
                    {boundaryShortLabel(lower)}
                  </DemoWorkbenchPill>
                );
              },
            })),
          ]}
        />
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={160} gap={12}>
        <DemoWorkbenchMetricCard
          label="Boundary status"
          value={metrics.fleetBounded ? "BOUNDED" : "UNBOUNDED"}
          color={metrics.fleetBounded ? "#34d399" : "#f87171"}
          sub={
            metrics.openCount === 0
              ? "No open boundaries"
              : `${metrics.openCount} OPEN boundary(ies)`
          }
        />
        <DemoWorkbenchMetricCard
          label="Total κ"
          value={metrics.totalKappa}
          color={metrics.totalKappa > 20 ? "#f87171" : "#2dd4bf"}
          sub={`internal: ${metrics.totalInternal} + inter: ${metrics.totalInter}`}
        />
        <DemoWorkbenchMetricCard
          label="Fleet status"
          value={
            metrics.allCertified && metrics.fleetBounded
              ? "CERTIFIED"
              : "NOT CERTIFIED"
          }
          color={
            metrics.allCertified && metrics.fleetBounded ? "#34d399" : "#fbbf24"
          }
          sub={`${metrics.certifiedCount}/${FLEET_SYSTEMS.length} systems certified`}
        />
      </DemoResponsiveGrid>

      <div className="flex gap-4 text-xs text-gray-500">
        {Object.entries(BOUNDARY_LABELS).map(([k, v]) => (
          <DemoWorkbenchPill
            key={k}
            variant={BOUNDARY_VARIANT[k as BoundaryType]}
          >
            {v}
          </DemoWorkbenchPill>
        ))}
      </div>
    </DemoWorkbenchStack>
  );
};

export default FleetSecurityPanel;
